import { cn } from "@/utils/helper";
import { Badge } from "./badge";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from "./command";
import { Popover, PopoverContent, PopoverTrigger } from "./popover";
import { LucideCheck, LucidePlusCircle } from "lucide-react";
import { useCommandState } from "cmdk";

interface MultiSelectProps {
  title?: string;
  options: {
    label: string;
    value: string;
    icon?: React.ComponentType<{ className?: string }>;
  }[];
  selectedValues: Set<string>;
  onSelectionChange: (selectedValues: Set<string>) => void;
  isDisabled?: boolean;
}

export function MultiSelect({
  title,
  options,
  selectedValues,
  onSelectionChange,
  isDisabled = false,
}: MultiSelectProps) {
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          className="flex h-9 grow-0 items-center gap-x-2 rounded-md border border-axiata-blue px-3 text-sm text-axiata-blue hover:bg-axiata-blue hover:text-white disabled:cursor-not-allowed disabled:border-[#E1E1E1] disabled:bg-[#F3F4F6] disabled:text-black disabled:opacity-100 dark:bg-neutral-50"
          disabled={isDisabled}
        >
          <LucidePlusCircle className="h-4 w-4" />
          {title}
          {selectedValues.size > 0 && (
            <>
              <Badge
                variant="secondary"
                className="rounded-sm px-1 font-normal lg:hidden"
              >
                {selectedValues.size}
              </Badge>
              <div className="hidden space-x-1 lg:flex">
                {selectedValues.size > 2 ? (
                  <Badge
                    variant="secondary"
                    className="rounded-sm px-1 font-normal"
                  >
                    {selectedValues.size} selected
                  </Badge>
                ) : (
                  options
                    .filter((option) => selectedValues.has(option.value))
                    .map((option) => (
                      <Badge
                        variant="secondary"
                        key={option.value}
                        className="rounded-sm px-1 font-normal"
                      >
                        {option.label}
                      </Badge>
                    ))
                )}
              </div>
            </>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent className="p-0" align="start">
        <Command>
          <CommandInput placeholder={title} />
          <CommandList>
            <CommandEmpty>No results found.</CommandEmpty>
            <CommandGroup className="max-h-[200px] overflow-y-auto">
              {options.map((option) => {
                const isSelected = selectedValues.has(option.value);
                return (
                  <CommandItem
                    key={option.value}
                    onSelect={() => {
                      const newSelectedValues = new Set(selectedValues);
                      if (isSelected) {
                        newSelectedValues.delete(option.value);
                      } else {
                        newSelectedValues.add(option.value);
                      }
                      onSelectionChange(newSelectedValues);
                    }}
                  >
                    <div
                      className={cn(
                        "mr-2 flex h-4 w-4 items-center justify-center rounded-sm border border-neutral-400",
                        isSelected
                          ? "bg-primary text-primary-foreground"
                          : "opacity-50 [&_svg]:invisible",
                      )}
                    >
                      <LucideCheck className={cn("h-4 w-4")} />
                    </div>
                    {option.icon && (
                      <option.icon className="text-muted-foreground mr-2 h-4 w-4" />
                    )}
                    <span>{option.label}</span>
                  </CommandItem>
                );
              })}
            </CommandGroup>

            <CommandSeparator />
            <CommandGroup forceMount>
              <SelectAll
                onSelectionChange={onSelectionChange}
                options={options}
              />
            </CommandGroup>

            <CommandSeparator />
            <CommandGroup forceMount>
              <CommandItem
                onSelect={() => onSelectionChange(new Set())}
                className="justify-center text-center"
              >
                Clear filters
              </CommandItem>
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

interface SelectAllProps {
  onSelectionChange: (selectedValues: Set<string>) => void;
  selectedValues?: Set<string>;
  options: {
    label: string;
    value: string;
    icon?: React.ComponentType<{ className?: string }>;
  }[];
}
function SelectAll({
  onSelectionChange,
  selectedValues,
  options,
}: SelectAllProps) {
  const filtered = useCommandState((state) => state.filtered);
  const newSelectedValues = new Set(selectedValues);
  return (
    <CommandItem
      onSelect={() => {
        const items: Map<string, number> = filtered.items;
        Array.from(items.values()).forEach((value, index) => {
          if (!value || !options[index]) return;

          newSelectedValues.add(options[index].value);
        });
        onSelectionChange(newSelectedValues);
      }}
      className="justify-center text-center"
      value="select-shown"
    >
      Select shown
    </CommandItem>
  );
}
